import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

import { useListAutomationSummaries } from '@/generated/hooks/grimoire/v1/api';
import {
  selectCurrentOrganizationId,
  selectCurrentWorkspaceId,
  useUserPreferencesStore,
} from '@/shared/stores';
import {
  useNudgeStore,
  selectSetNudgeCondition,
  selectClearNudgeCondition,
} from '../nudges.store';
import { ENudgeId } from '../types';
import { isNudgeDismissed } from '../persistence';

/**
 * Evaluates the "create first draft" nudge condition and syncs it
 * to the nudge store.
 *
 * The nudge is active when:
 * - The user has zero automations (no drafts and no published)
 * - The user is NOT on any drafts-related page and NOT on onboarding pages
 * - The nudge has not been dismissed
 *
 * Place this hook once in a layout-level component that renders
 * on every authenticated page (e.g., MainLayout).
 */
export function useCreateFirstDraftNudge() {
  const orgId = useUserPreferencesStore(selectCurrentOrganizationId);
  const workspaceId = useUserPreferencesStore(selectCurrentWorkspaceId);
  const setCondition = useNudgeStore(selectSetNudgeCondition);
  const clearCondition = useNudgeStore(selectClearNudgeCondition);
  const { pathname } = useLocation();

  const isOnDraftsPage = pathname.includes('/drafts');
  const isOnOnboardingPage = pathname.startsWith('/onboarding');
  const alreadyDismissed = isNudgeDismissed(ENudgeId.CREATE_FIRST_DRAFT);

  const { data, isSuccess } = useListAutomationSummaries(
    orgId,
    workspaceId,
    { page_size: 1 },
    {
      query: {
        enabled: !!orgId && !!workspaceId && !alreadyDismissed,
        staleTime: 5 * 60 * 1000,
      },
    },
  );

  const hasAutomations = isSuccess
    ? (data?.data?.automation_summaries?.length ?? 0) > 0
    : undefined;

  // eslint-disable-next-line no-restricted-syntax -- reason: Sync server state (has automations) + route (is on drafts/onboarding page) to nudge store
  useEffect(() => {
    if (isNudgeDismissed(ENudgeId.CREATE_FIRST_DRAFT)) {
      clearCondition(ENudgeId.CREATE_FIRST_DRAFT);
      return;
    }
    if (hasAutomations === undefined) return;
    setCondition(
      ENudgeId.CREATE_FIRST_DRAFT,
      !hasAutomations && !isOnDraftsPage && !isOnOnboardingPage,
    );
  }, [
    hasAutomations,
    isOnDraftsPage,
    isOnOnboardingPage,
    setCondition,
    clearCondition,
  ]);
}
